import { Tooltip } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';

import LinkButton from '../../../../components/LinkButton';
import ProgressIndicator from '../../../../components/ProgressIndicator';
import { formatarData } from '../../../../helpers/data';
import {
  formatarMoeda,
  formatarPorcentagemSufixoEsquerda,
  formatarTonelada,
  toTitleCase,
} from '../../../../helpers/formatador';
import apiShipper from '../../../../services/apiShipper';
import colors from '../../../../styles/colors';
import {
  AgendamentoCollapse,
  ItemAgendamento,
  Panel,
} from '../../@components/AgendamentoCollapse';
import { useAgendamentoAlteracaoContext } from '../../Contextos/AgendamentoAlteracaoContexto';
import ClassificacaoLoteEnum from '../../Enum/ClassificacaoLoteEnum';
import StatusLoteEnum from '../../Enum/StatusLoteEnum';
import TipoDirecionamentoEnum from '../../Enum/TipoDirecionamentoEnum';
import AgendamentoAlteracaoActionTypes from '../../Reducers/AgendamentoAlteracao/actions';
import Actions from './Actions';
import Compilados from './Compilados';
import {
  ActionContainer,
  BodyContainer,
  CampoLote,
  CampoLoteLabel,
  CampoLoteValor,
  DetalhesLote,
  HeaderLote,
  InfoContainer,
  Lote,
  StatusLote,
  Tag,
} from './styles';

const getColorTipoLote = tipo => {
  switch (tipo) {
    case TipoDirecionamentoEnum.MARKETPLACE:
      return {
        background: colors.$brandcolor_primary_dark_green_12,
        textColor: colors.$brandcolor_primary_dark_green_11,
      };
    default:
      return {
        background: colors.$auxiliary_sky_6,
        textColor: colors.$auxiliary_sky_7,
      };
  }
};

const getColorStatusLote = tipo => {
  switch (tipo) {
    case StatusLoteEnum.CARREGANDO:
      return {
        background: colors.$brandcolor_primary_dark_green_12,
        textColor: colors.$brandcolor_primary_dark_green_11,
      };
    case StatusLoteEnum.SUSPENSO:
      return {
        background: colors.$auxiliary_yolk_4,
        textColor: colors.$auxiliary_yolk_3,
      };
    default:
      return {
        background: colors.$auxiliary_berry_13,
        textColor: colors.$auxiliary_berry_12,
      };
  }
};

const getDirecionamento = lote => {
  if (lote.tipoDirecionamento === TipoDirecionamentoEnum.MARKETPLACE) {
    return 'Público (TMOV)';
  }

  return (
    lote.nomeGrupoFrotista ||
    lote.nomeFantasiaProprietario ||
    lote.razaoSocialTransportadora
  );
};

const getFreteEmpresa = lote => {
  if (lote.tipoDirecionamento === TipoDirecionamentoEnum.MARKETPLACE) {
    if (lote.freteMotoristaPj !== lote.freteMotoristaPf) {
      return `${formatarMoeda(lote.freteTotalPj)} / ${formatarMoeda(
        lote.freteTotalPf
      )}`;
    }
    return formatarMoeda(lote.freteTotalPj);
  }
  return formatarMoeda(lote.freteEmpresaShipper);
};

const getFreteMotorista = lote => {
  if (lote.tipoDirecionamento === TipoDirecionamentoEnum.MARKETPLACE) {
    if (lote.freteMotoristaPj !== lote.freteMotoristaPf) {
      return `${formatarMoeda(lote.freteMotoristaPj)} / ${formatarMoeda(
        lote.freteMotoristaPf
      )}`;
    }
    return formatarMoeda(lote.freteMotoristaPj);
  }
  return '-';
};

const Lotes = ({
  idDemanda,
  detalhesDemanda,
  compilados,
  endereco,
  dadosPagador,
}) => {
  const {
    lotes,
    razaoSocialClientePagador,
    enderecoRecebedor,
    enderecoExpedidor,
    tipoPagamento,
  } = detalhesDemanda;
  const { agendamentosAlteracao, dispatch } = useAgendamentoAlteracaoContext();
  const emails = lotes.flatMap(lote => lote.filiaisEmail);
  const statusLotes = lotes.map(lote => lote.idStatus);
  const isCarregando = statusLotes.every(
    status => status === StatusLoteEnum.CARREGANDO
  );
  const isSuspenso = statusLotes.every(
    status => status === StatusLoteEnum.SUSPENSO
  );
  const isFinalizado = statusLotes.every(
    status => status === StatusLoteEnum.FINALIZADO
  );

  const dadosAcoesEmMassa = {
    cliente_razao_social: razaoSocialClientePagador,
    origem: `${toTitleCase(String(enderecoExpedidor[0]?.cidade))} (${
      enderecoExpedidor[0]?.estado
    })`,
    destino: `${toTitleCase(String(enderecoRecebedor[0]?.cidade))} (${
      enderecoRecebedor[0]?.estado
    })`,
    email_filiais: emails,
    endereco,
    dadosPagador,
    tipo_pagamento: tipoPagamento,
    validacaoStatusLote: {
      isCarregando,
      isSuspenso,
      isFinalizado,
    },
  };

  useEffect(() => {
    (async () => {
      const { data } = await apiShipper.get('alteracao-demanda', {
        params: {
          id_demanda: idDemanda,
        },
      });

      dispatch({
        type: AgendamentoAlteracaoActionTypes.SET_AGENDAMENTOS,
        payload: data,
      });
    })();
  }, [idDemanda, dispatch]);

  const hasAnyAgendamento = Object.keys(agendamentosAlteracao).length > 0;

  return (
    <BodyContainer>
      {hasAnyAgendamento && (
        <AgendamentoCollapse expandIconPosition="right">
          <Panel>
            {agendamentosAlteracao.map(agendamento => (
              <ItemAgendamento key={agendamento.id} agendamento={agendamento} />
            ))}
          </Panel>
        </AgendamentoCollapse>
      )}

      <Compilados compilados={compilados} />
      <Actions
        idDemanda={Number(idDemanda)}
        dadosAcoesEmMassa={dadosAcoesEmMassa}
      />
      {lotes &&
        lotes.map(lote => (
          <Lote key={lote.idLote}>
            <HeaderLote>
              <InfoContainer>
                <b>{lote.idLote}</b>
                <Tag
                  color={getColorTipoLote(lote.tipoDirecionamento).background}
                  texto={getColorTipoLote(lote.tipoDirecionamento).textColor}
                >
                  {
                    TipoDirecionamentoEnum.getTipo(lote.tipoDirecionamento)
                      .label
                  }
                </Tag>
              </InfoContainer>
              <ActionContainer>
                <LinkButton
                  path={{
                    pathname: `/lotes/demanda/${idDemanda}`,
                    state: { step: 3 },
                  }}
                  underline={false}
                >
                  Editar
                </LinkButton>
              </ActionContainer>
            </HeaderLote>
            <DetalhesLote>
              <CampoLote>
                <CampoLoteLabel>Direcionado para</CampoLoteLabel>
                <Tooltip title={getDirecionamento(lote)} placement="bottomLeft">
                  <CampoLoteValor>{getDirecionamento(lote)}</CampoLoteValor>
                </Tooltip>
              </CampoLote>
              {lote?.classificacaoLote ===
                ClassificacaoLoteEnum.LIVRE_CONCORRENCIA && (
                <CampoLote>
                  <CampoLoteLabel>Volume carregado</CampoLoteLabel>
                  <CampoLoteValor>
                    {formatarTonelada(lote?.embarcado)}
                  </CampoLoteValor>
                </CampoLote>
              )}
              {lote?.classificacaoLote === ClassificacaoLoteEnum.COTA && (
                <CampoLote>
                  <CampoLoteLabel>Saldo/Volume</CampoLoteLabel>
                  <CampoLoteValor>
                    {`${formatarTonelada(lote.saldo)}/${formatarTonelada(
                      lote.volume
                    )}`}
                  </CampoLoteValor>
                </CampoLote>
              )}
              <CampoLote>
                <CampoLoteLabel>Cadência do dia</CampoLoteLabel>
                <CampoLoteValor>
                  {formatarTonelada(lote.cadencia)}
                </CampoLoteValor>
              </CampoLote>
              <CampoLote>
                <CampoLoteLabel>Aderência do dia</CampoLoteLabel>
                <CampoLoteValor>
                  <ProgressIndicator
                    progress={lote.percentualAderenciaDia}
                    label={`${formatarPorcentagemSufixoEsquerda(
                      `${lote.percentualAderenciaDia}`,
                      '',
                      2,
                      ''
                    )}%`}
                  />
                </CampoLoteValor>
              </CampoLote>
              <CampoLote>
                <CampoLoteLabel>Data início</CampoLoteLabel>
                <CampoLoteValor>{formatarData(lote.dataInicio)}</CampoLoteValor>
              </CampoLote>
              <CampoLote>
                <CampoLoteLabel>Frete Empresa PJ/PF</CampoLoteLabel>
                <CampoLoteValor>{getFreteEmpresa(lote)}</CampoLoteValor>
              </CampoLote>
              <CampoLote>
                <CampoLoteLabel>Frete motorista PJ/PF</CampoLoteLabel>
                <CampoLoteValor>{getFreteMotorista(lote)}</CampoLoteValor>
              </CampoLote>
              <StatusLote>
                <Tag
                  color={getColorStatusLote(lote.idStatus).background}
                  texto={getColorStatusLote(lote.idStatus).textColor}
                >
                  {StatusLoteEnum.getTipo(lote.idStatus).label}
                </Tag>
              </StatusLote>
            </DetalhesLote>
          </Lote>
        ))}
    </BodyContainer>
  );
};

export default Lotes;

const rotaProps = PropTypes.arrayOf(
  PropTypes.shape({
    cidade: PropTypes.string,
    estado: PropTypes.string,
  })
);

Lotes.propTypes = {
  idDemanda: PropTypes.string.isRequired,
  detalhesDemanda: PropTypes.shape({
    razaoSocialClientePagador: PropTypes.string,
    enderecoRecebedor: rotaProps,
    enderecoExpedidor: rotaProps,
    tipoPagamento: PropTypes.number,
    lotes: PropTypes.arrayOf(
      PropTypes.shape({
        idLote: PropTypes.number,
        tipoDirecionamento: PropTypes.number,
        volume: PropTypes.number,
        volumeMovimentado: PropTypes.number,
        cadencia: PropTypes.number,
        dataInicio: PropTypes.string,
        freteMotoristaPf: PropTypes.number,
        freteMotoristaPj: PropTypes.number,
        freteTotalPf: PropTypes.number,
        freteTotalPj: PropTypes.number,
        freteTotal: PropTypes.number,
        idStatus: PropTypes.number,
        percentualAderenciaDia: PropTypes.number,
        embarcado: PropTypes.number,
      })
    ),
  }),
  compilados: PropTypes.shape({
    volume: 0,
    saldo: 0,
    cadenciaDia: 0,
    aderenciaDiaPercentual: 0,
    aderenciaDiaTon: 0,
    aderenciaDia: 0,
    cotas: 0,
  }).isRequired,
  endereco: PropTypes.objectOf(PropTypes.any).isRequired,
  dadosPagador: PropTypes.objectOf(PropTypes.any).isRequired,
};

Lotes.defaultProps = {
  detalhesDemanda: null,
};
